import { cn } from "@gettactic/helpers";
import { createMapLink } from "@gettactic/helpers";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@cmp-src/primitives/tooltip";
import { Button } from "@cmp-src/primitives/button";
import { ViewDeskIcon } from "@cmp-src/icons/TacticIcons/VIewDeskIcon";
import { WorkingLocationPresenceIcon } from "./WorkingLocationPresenceIcon";
import { IResource, IWorkingLocation, IUserFull } from "@gettactic/api";

import { AvailableStatus } from "@cmp-src/icons/TacticIcons/statusIcons";
import { useBreakpoint } from "../../../../../helpers/src/hooks/useBreakpoint";
import { dateStringToLocalTime } from "@gettactic/helpers/src/date-time/dateStringToLocalTime";
import { useRouter } from "../../../useRouter";

interface CellProps {
  status: string;
  resource?: IResource;
  officeName?: string;
  officeId?: string;
  date: string;
  startTime?: string;
  endTime?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isFlat?: boolean;
}

export interface ExtendedUser {
  user: IUserFull;
  week: Record<string, IWorkingLocation>;
}

export const OfficeStatusCell = ({
  status,
  resource,
  officeName,
  officeId,
  date,
  startTime,
  endTime,
  onClick,
  isFlat = false
}: CellProps) => {
  const router = useRouter();
  const hasDesk = status === "inOffice";
  const isRemote = status === AvailableStatus.remote;
  const isOOO = status === AvailableStatus.ooo;
  const isUnknown = status === AvailableStatus.unknown;
  const notAnOffice = isRemote || isOOO || isUnknown;
  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");

  const label = hasDesk
    ? null
    : isRemote
      ? "Working from home"
      : isOOO
        ? "Out of office"
        : "Not specified";

  const link = createMapLink({
    date: startTime ? dateStringToLocalTime(startTime) : date,
    officeId,
    resourceId: resource?.id,
    floorId: resource?.parent_id ?? undefined,
    startTime: startTime ? dateStringToLocalTime(startTime) : undefined,
    endTime: endTime ? dateStringToLocalTime(endTime) : undefined
  });

  return (
    <Tooltip asToggleTip={!isMd}>
      <TooltipTrigger asChild>
        <Button
          className={
            !isFlat
              ? cn(
                  "dashboard-status-cell",
                  "!p-0 !rounded-v2 m-auto",
                  "flex h-[36px] bg-neutral hover:bg-neutral-100",
                  "w-full max-w-full items-center justify-center align-center",
                  hasDesk ? "text-icon-gray" : "text-disabled",
                  officeName ? "cursor-pointer" : "cursor-default",
                  "[&_svg]:h-[16px] [&_svg]:w-[16px]"
                )
              : undefined
          }
          onClick={
            onClick ||
            (() => {
              officeName && router.push(link);
            })
          }
          variant="ghost"
        >
          <WorkingLocationPresenceIcon status={status} />
        </Button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className={
          "flex min-w-[7rem] max-w-xs whitespace-normal break-words justify-center gap-0.5 rounded-lg bg-hint-bg px-2.5 py-1.5 text-hint-text text-xs shadow-xl"
        }
      >
        {notAnOffice ? (
          <LabelWrapper>{label}</LabelWrapper>
        ) : (
          <LabelWrapper
            onClick={() => {
              officeName && router.push(link);
            }}
          >
            <div className="flex flex-wrap items-center gap-0.5 whitespace-normal break-words text-xs text-center">
              {officeName || "Office"} {hasDesk && "-"}{" "}
              {hasDesk && !!resource && (resource?.name ?? "Resource")}
            </div>
          </LabelWrapper>
        )}
      </TooltipContent>
    </Tooltip>
  );
};

const LabelWrapper = ({
  children,
  onClick
}: {
  children: React.ReactNode;
  onClick?: () => void;
}) => {
  return (
    <div
      className={cn(
        "flex items-center gap-0.5 align-center align-items text-xs",
        !!onClick && "cursor-pointer"
      )}
      onClick={onClick}
    >
      {children}
      {!!onClick && <ViewDeskIcon className="h-4 w-4" />}
    </div>
  );
};
